import { useEffect, useState, useCallback } from 'react';
import { get, post } from '../../../../services/apiController';
import { useForm, useWatch } from 'react-hook-form';
import { Modal, Form, Row, Col, FloatingLabel, Button, Table } from 'react-bootstrap';
import Company from '../../../../components/company';
import Factory from '../../../../components/factory';
import { COMPANY_ID, units } from '../../../../util/constant';
import Vendor from '../../../../components/vendor';
import Item from '../../../../components/item';
import currency from 'currency.js';
import { HiOutlineTrash } from 'react-icons/hi2';
import { successToast, errorToast } from '../../../../util/toast.util';
import DatePickerComponent from '../../../../components/common/date-picker';

export default function AddInvDebitNoteModal(props) {
  const { open, onClose, grnId, factories } = props;

  const [invItems, setInvItems] = useState([]);

  const [calculations, setCalculations] = useState({
    amountReturningWithoutGST: 0,
    amountReturningCGST: 0,
    amountReturningSGST: 0,
    amountReturningIGST: 0,
    totalAmountWithGST: 0,
  });

  const { register, reset, handleSubmit, getValues, setValue, control, watch } = useForm({
    defaultValues: {
      debitNoteNumber: '',
      grnNumber: '',
      debitNoteDate: '',
      companyId: '',
      factoryId: '',
      grnDate: '',
      vendorId: '',
      invoiceDate: '',
      supplierInvoiceNumber: '',
      VendorMaster: {
        name: '',
        gst_num: '',
        address: '',
        contact_num: '',
      },
      assetsInfo: [],
      remarks: '',
    },
  });

  const debitNoteDate = watch('debitNoteDate');

  const loadGRN = async () => {
    if (!grnId) {
      return;
    }
    try {
      const response = await get(`/inv/grns/${grnId}?conditionType=edit`);
      reset(response.data);
      setValue('debitNoteDate', new Date().toISOString().split('T')[0]);
    } catch (error) {
      console.log(error);
    }
  };

  const onDebitNoteSubmit = async (data) => {
    try {
      // Preparing data for submission
      const { id, ...rest } = data;
      const items = rest.assetsInfo.map((item) => ({
        itemId: item.itemId,
        quantityReturning: Number(item.quantityReturning),
      }));
      await post(
        {
          debitNoteDate: data.debitNoteDate,
          debitNoteNumber: data.debitNoteNumber,
          remarks: data.remarks,
          grnId: id,
          items,
        },
        '/inv/debit-notes'
      );
      successToast('Debit note created successfully');
      onClose();
    } catch (error) {
      errorToast(error.response.data.data.message);
    }
  };

  const loadItems = async () => {
    try {
      const response = await get(`/inv/items`);
      setInvItems(response.data.rows);
    } catch (error) {
      errorToast(error.response.data.data.message);
    }
  };

  const loadDebitNoteNo = useCallback(async () => {
    if (!grnId || !debitNoteDate) {
      return;
    }
    try {
      const response = await get(`/inv/debit-notes/generate-debit-note-no?grnId=${grnId}&debitNoteDate=${debitNoteDate}`);
      setValue('debitNoteNumber', response.data);
    } catch (error) {
      console.log(error);
    }
  }, [grnId, debitNoteDate]);

  const onRemovingReturningItem = (index) => {
    const assetsInfo = getValues('assetsInfo');
    assetsInfo.splice(index, 1);
    setValue('assetsInfo', assetsInfo);
  };

  const onAddingReturningItem = (e, index, inputField) => {
    let amountReturningTotal;
    const amountReturningWithoutGST = currency(e.target.value).multiply(currency(inputField.rate));
    setValue(`assetsInfo.${index}.amountReturningWithoutGST`, amountReturningWithoutGST.value);
    if (inputField.gstType === 'local') {
      const amountReturningCGST = amountReturningWithoutGST.multiply(currency(inputField.cgst)).divide(100);
      setValue(`assetsInfo.${index}.amountReturningCGST`, amountReturningCGST.value);
      const amountReturningSGST = amountReturningWithoutGST.multiply(currency(inputField.sgst)).divide(100);
      setValue(`assetsInfo.${index}.amountReturningSGST`, amountReturningSGST.value);
      amountReturningTotal = amountReturningWithoutGST.add(amountReturningCGST).add(amountReturningSGST);
    } else {
      const amountReturningIGST = amountReturningWithoutGST.multiply(currency(inputField.igst)).divide(100);
      setValue(`assetsInfo.${index}.amountReturningIGST`, amountReturningIGST.value);
      amountReturningTotal = amountReturningWithoutGST.add(amountReturningIGST);
    }
    setValue(`assetsInfo.${index}.amountReturningTotal`, amountReturningTotal.value);
  };

  const assetInfo = useWatch({
    name: 'assetsInfo',
    control,
  });

  // Calculating when assetInfo changes
  useEffect(() => {
    if (assetInfo) {
      let totalAmountWithoutGST = currency(0);
      let sgstAmount = currency(0);
      let cgstAmount = currency(0);
      let igstAmount = currency(0);
      let totalAmountWithGST = currency(0);
      assetInfo.forEach((item) => {
        totalAmountWithoutGST = totalAmountWithoutGST.add(currency(item.amountReturningWithoutGST));
        sgstAmount = sgstAmount.add(currency(item.amountReturningSGST));
        cgstAmount = cgstAmount.add(currency(item.amountReturningCGST));
        igstAmount = igstAmount.add(currency(item.amountReturningIGST));
        totalAmountWithGST = totalAmountWithoutGST.add(sgstAmount).add(cgstAmount).add(igstAmount);
      });
      setCalculations({
        amountReturningWithoutGST: totalAmountWithoutGST,
        amountReturningCGST: cgstAmount,
        amountReturningSGST: sgstAmount,
        amountReturningIGST: igstAmount,
        totalAmountWithGST: totalAmountWithGST,
      });
    }
  }, [assetInfo]);

  useEffect(() => {
    loadGRN();
    loadItems();
  }, [grnId]);

  useEffect(() => {
    if (open) {
      loadDebitNoteNo();
    }
  }, [open, debitNoteDate, grnId]);

  return (
    <Modal size="lg" show={open} backdrop="static" keyboard={false} className="request-modal">
      <Modal.Header>
        <Modal.Title>Debit Note</Modal.Title>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
      </Modal.Header>
      <Modal.Body>
        <Form autoComplete="off" onSubmit={handleSubmit(onDebitNoteSubmit)}>
          <h6>Basic Info</h6>
          <Row>
            <Col xs={6}>
              <DatePickerComponent
                placeholderText="Debit Note Date"
                className="required-field"
                selectedDate={watch('debitNoteDate')}
                onDateChange={(date) => {
                  setValue('debitNoteDate', date || '');
                }}
              />
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Debit Note Number" className="mb-3 required-field">
                <Form.Control readOnly className="input" type="text" placeholder="Debit Note Number" {...register('debitNoteNumber')} />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="GRN Date" className="mb-3">
                <Form.Control disabled className="input" type="date" placeholder="GRN Date" {...register('grnDate')} />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Company" className="mb-3">
                <Company disabled company={1} onChange={() => {}} label="Company" {...register('companyId')} />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label={'Factory'} className="mb-3">
                <Factory
                  disabled={true}
                  factoryId={getValues('factoryId')}
                  factories={factories}
                  company={COMPANY_ID}
                  onChange={() => {}}
                  label="Factory"
                />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="GRN No." className="mb-3">
                <Form.Control
                  disabled
                  className="input"
                  name="grnNumber"
                  value={getValues('grnNumber')}
                  type="text"
                  placeholder="GRN No."
                />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <Vendor isDisabled name="vendorId" vendorId={getValues('vendorId')} handleVendorChange={(vendorId) => {}} />
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Supplier Address" className="mb-3">
                <Form.Control type="text" value={getValues('VendorMaster.address')} placeholder="Supplier Address" disabled />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Supplier Contact number" className="mb-3">
                <Form.Control type="text" value={getValues('VendorMaster.contact_num')} placeholder="Supplier Contact number" disabled />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Supplier GSTIN" className="mb-3">
                <Form.Control type="text" value={getValues('VendorMaster.gst_num')} placeholder="Supplier GSTIN" disabled />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Invoice Date" className="mb-3">
                <Form.Control type="date" value={getValues('invoiceDate')} placeholder="Invoice Date" disabled />
              </FloatingLabel>
            </Col>
            <Col xs={6}>
              <FloatingLabel controlId="floatingInput" label="Supplier Invoice Number" className="mb-3">
                <Form.Control type="text" value={getValues('supplierInvoiceNumber')} placeholder="Supplier Invoice Number" disabled />
              </FloatingLabel>
            </Col>
          </Row>
          <h6>Debit Note Details</h6>
          {(assetInfo || []).map((inputField, index) => {
            return (
              <>
                <div key={inputField.id} className="asset-info">
                  <Row className="align-items-center" wrap={true}>
                    <Col xs={4} className="mb-3">
                      <Item
                        invItems={invItems}
                        itemId={inputField.itemId}
                        onChange={(itemId) => {}}
                        name="itemId"
                        isDisabled={true}
                        query=""
                        locationId={116}
                      />
                    </Col>
                    <Col xs={2}>
                      <FloatingLabel controlId="floatingInput" label="HSN Code" className="mb-3">
                        <Form.Control disabled type="text" name="hsnCode" value={inputField.hsnCode} placeholder="HSN Code" />
                      </FloatingLabel>
                    </Col>
                    <Col xs={2}>
                      <FloatingLabel controlId="floatingInput" label="Unit" className="mb-3">
                        <select
                          disabled
                          name="unit"
                          value={inputField.unit}
                          onChange={(e) => {}}
                          aria-label="Floating label select example"
                          className="form-select"
                        >
                          <option value="">Select Unit</option>
                          {units.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item?.label}
                            </option>
                          ))}
                        </select>
                      </FloatingLabel>
                    </Col>
                    <Col xs={4}>
                      <FloatingLabel controlId="floatingInput" label="Description" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          name="description"
                          value={inputField.description}
                          onChange={(e) => {}}
                          placeholder="Description"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={4}>
                      <FloatingLabel controlId="floatingInput" label="Quantity As Per Invoice" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          name="quantityAsPerInvoice"
                          value={inputField.quantityAsPerInvoice}
                          onChange={(e) => {}}
                          placeholder="Quantity As Per Invoice"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={4}>
                      <FloatingLabel controlId="floatingInput" label="Quantity Received" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          placeholder="Quantity Received"
                          {...register(`assetsInfo.${index}.quantityReceived`)}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={4}>
                      <FloatingLabel controlId="floatingInput" label="Rate Per Unit" className="mb-3">
                        <Form.Control
                          type="text"
                          name="rate"
                          disabled
                          value={inputField.rate}
                          onChange={(e) => {}}
                          placeholder="Rate Per Unit"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={4}>
                      <FloatingLabel controlId="floatingInput" label="GST Type" className="mb-3">
                        <Form.Select className="input" disabled value={inputField.gstType} name="gstType" onChange={(e) => {}}>
                          <option value="">Select GST</option>
                          <option value="local">Local GST</option>
                          <option value="interstate">Inter State GST</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col xs={8}>
                      <Row>
                        <Col xs={6} hidden={inputField.gstType !== 'local'}>
                          <FloatingLabel controlId="floatingInput" label="CGST" className="mb-3">
                            <Form.Select disabled className="input" value={inputField.cgst} name="cgst" onChange={(e) => {}}>
                              <option value="">Select CGST</option>
                              <option value="0">0%</option>
                              <option value="1.5">1.5%</option>
                              <option value="2.5">2.5%</option>
                              <option value="6">6%</option>
                              <option value="9">9%</option>
                              <option value="14">14%</option>
                            </Form.Select>
                          </FloatingLabel>
                        </Col>
                        <Col xs={6} hidden={inputField.gstType !== 'local'}>
                          <FloatingLabel controlId="floatingInput" label="SGST" className="mb-3">
                            <Form.Select disabled className="input" value={inputField.sgst} name="sgst" onChange={(e) => {}}>
                              <option value="">Select SGST</option>
                              <option value="0">0%</option>
                              <option value="1.5">1.5%</option>
                              <option value="2.5">2.5%</option>
                              <option value="6">6%</option>
                              <option value="9">9%</option>
                              <option value="14">14%</option>
                            </Form.Select>
                          </FloatingLabel>
                        </Col>
                        <Col xs={6} hidden={inputField.gstType === 'local'}>
                          <FloatingLabel controlId="floatingInput" label="IGST" className="mb-3">
                            <Form.Select disabled className="input" value={inputField.igst} name="igst" onChange={(e) => {}}>
                              <option value="">Select IGST</option>
                              <option value="0">0%</option>
                              <option value="3">3%</option>
                              <option value="5">5%</option>
                              <option value="12">12%</option>
                              <option value="18">18%</option>
                              <option value="28">28%</option>
                            </Form.Select>
                          </FloatingLabel>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel controlId="floatingInput" label="Total without GST" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          name="amount"
                          value={currency(inputField.quantityReceived * inputField.rate)}
                          onChange={(e) => {}}
                          placeholder="Amount"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} hidden={inputField.gstType !== 'local'}>
                      <FloatingLabel controlId="floatingInput" label="Total CGST" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          name="amount"
                          value={currency(inputField.rate * inputField.quantityReceived)
                            .multiply(inputField.cgst)
                            .divide(100)}
                          onChange={(e) => {}}
                          placeholder="Amount"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} hidden={inputField.gstType !== 'local'}>
                      <FloatingLabel controlId="floatingInput" label="Total SGST" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          name="amount"
                          value={currency((inputField.quantityReceived * inputField.rate * inputField.sgst) / 100)}
                          placeholder="Amount"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} hidden={inputField.gstType !== 'interstate'}>
                      <FloatingLabel controlId="floatingInput" label="Total IGST" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          name="amount"
                          value={currency((inputField.quantityReceived * inputField.rate * inputField.igst) / 100)}
                          placeholder="Amount"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel controlId="floatingInput" label="Invoice Amount" className="mb-3">
                        <Form.Control type="text" disabled name="amount" value={currency(inputField.invoiceAmount)} placeholder="Amount" />
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <h6 className="mb-3">Returning Details</h6>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel controlId="floatingInput" label="Quantity Returning" className="mb-3 required-field">
                        <Form.Control
                          type="text"
                          placeholder="Quantity Returning"
                          {...register(`assetsInfo.${index}.quantityReturning`)}
                          onChange={(e) => onAddingReturningItem(e, index, inputField)}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel controlId="floatingInput" label="Amount Without GST" className="mb-3">
                        <Form.Control
                          type="text"
                          disabled
                          placeholder="CGST"
                          {...register(`assetsInfo.${index}.amountReturningWithoutGST`)}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} hidden={inputField.gstType !== 'local'}>
                      <FloatingLabel controlId="floatingInput" label="Toal CGST" className="mb-3">
                        <Form.Control disabled type="text" placeholder="CGST" {...register(`assetsInfo.${index}.amountReturningCGST`)} />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} hidden={inputField.gstType !== 'local'}>
                      <FloatingLabel controlId="floatingInput" label="Total SGST" className="mb-3">
                        <Form.Control disabled type="text" placeholder="SGST" {...register(`assetsInfo.${index}.amountReturningSGST`)} />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} hidden={inputField.gstType !== 'interstate'}>
                      <FloatingLabel controlId="floatingInput" label="Total IGST" className="mb-3">
                        <Form.Control disabled type="text" placeholder="IGST" {...register(`assetsInfo.${index}.amountReturningIGST`)} />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel controlId="floatingInput" label="Total Amount" className="mb-3">
                        <Form.Control disabled type="text" placeholder="SGST" {...register(`assetsInfo.${index}.amountReturningTotal`)} />
                      </FloatingLabel>
                    </Col>
                    <Col xs={1} className="d-flex align-items-center">
                      <HiOutlineTrash onClick={() => onRemovingReturningItem(index)} className="text-danger mb-3 cursor-pointer" />
                    </Col>
                  </Row>
                </div>
                <hr />
              </>
            );
          })}
          <Row>
            <Col xs={7}>
              <FloatingLabel controlId="remarks" label="Remarks" className="mb-3 required-field">
                <Form.Control as="textarea" style={{ height: '100px' }} {...register('remarks')} />
              </FloatingLabel>
              <Button type="submit" variant="primary">
                Create Debit Note
              </Button>
            </Col>
            <Col xs={5}>
              <Table className="table table-bordered">
                <tbody>
                  <tr>
                    <td>Total Without GST</td>
                    <td className="text-end">{currency(calculations.amountReturningWithoutGST).format({ symbol: '₹', precision: 2 })}</td>
                  </tr>
                  <tr>
                    <td>CGST</td>
                    <td className="text-end">{currency(calculations.amountReturningCGST).format({ symbol: '₹', precision: 2 })}</td>
                  </tr>
                  <tr>
                    <td>SGST</td>
                    <td className="text-end">{currency(calculations.amountReturningSGST).format({ symbol: '₹', precision: 2 })}</td>
                  </tr>
                  <tr>
                    <td>IGST</td>
                    <td className="text-end">{currency(calculations.amountReturningIGST).format({ symbol: '₹', precision: 2 })}</td>
                  </tr>
                  <tr>
                    <td className="color-red">Total</td>
                    <td className="text-end fw-bold">{currency(calculations.totalAmountWithGST).format({ symbol: '₹', precision: 2 })}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
